/**
 * Home autenticada.
 *
 * Estado actual (Sprint 6 Bloque 4):
 * - Header: nombre del user.
 * - Card "Mi plan" con CTA "Ver plan" o "Crear plan" según haya plan activo.
 * - Card de Strava (conectar / sincronizar / desconectar).
 * - Toast inline para feedback compartido (post-callback Strava,
 *   post-sync, post-disconnect, errores).
 * - Botón logout al pie.
 *
 * Sprints futuros:
 * - Sprint 5: card "último análisis".
 *
 * Query params manejados:
 * - `?strava=connected&imported=N` (post-callback OAuth Strava OK):
 *   muestra toast con N actividades importadas, luego limpia la URL
 *   con history.replaceState para que no se quede pegado al refrescar.
 * - `?strava=error&code=X` (post-callback OAuth Strava error):
 *   muestra toast de error traducido, limpia URL.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { StravaCard } from "@/components/strava/StravaCard";
import { useAuth } from "@/hooks/useAuth";
import { t } from "@/i18n/es";
import { getOnboardingStatus } from "@/api/onboarding";
import { getActivePlan } from "@/api/training";

interface Toast {
  id: number;
  message: string;
  kind: "success" | "error";
}

let toastSeq = 0;

export function Home() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Sprint 13 S13.10b: estado del plan para el CTA de Home.
  //   null     = aún no resuelto.
  //   "active" = hay plan activo → "Ver plan".
  //   "decision" = sin plan pero con decisión pendiente (recovery terminó /
  //                progresión venció) → CTA "Elige tu próximo objetivo" → /plan.
  //   "none"   = sin plan ni decisión → "Crear plan".
  const [planState, setPlanState] = useState<
    "active" | "decision" | "none" | null
  >(null);
  // pollOnMount se decide UNA VEZ al primer render — si el user llega
  // con `?strava=connected&import_status=pending`, StravaCard arranca
  // polling al montar. Lo guardamos en estado para que el limpiado
  // posterior de la URL no afecte al valor.
  const [stravaCallbackPending] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("strava") === "connected" &&
      params.get("import_status") === "pending"
    );
  });

  const showToast = useCallback(
    (message: string, kind: "success" | "error" = "success") => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t_) => t_.id !== id));
      }, 5000);
    },
    [],
  );

  // Onboarding gate (Sprint 6 Bloque 5): si el user no ha completado
  // onboarding, redirigimos a /onboarding antes de renderizar nada
  // útil. Si la petición falla, degradamos a "sin onboarding" para
  // forzar el flujo (mejor pedir al user que rellene perfil que dejarle
  // entrar a una home rota).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await getOnboardingStatus();
        if (cancelled) return;
        if (!status.completed) {
          navigate("/onboarding", { replace: true });
        }
      } catch {
        if (!cancelled) navigate("/onboarding", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Fetch del plan activo al montar la home — para decidir si mostramos
  // "Ver plan" o "Crear plan". Si falla la petición, degradamos a
  // estado "sin plan" (el user puede ir a /plan e intentar de nuevo allí).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Sprint 13 S13.10b: getActivePlan devuelve { plan, ...flags }.
        // Home refleja el estado: plan activo / decisión pendiente / sin plan.
        const {
          plan,
          postRecoveryOptionsAvailable,
          progressionDistanceChoicePending,
        } = await getActivePlan();
        if (cancelled) return;
        if (plan !== null) {
          setPlanState("active");
        } else if (
          postRecoveryOptionsAvailable ||
          progressionDistanceChoicePending
        ) {
          setPlanState("decision");
        } else {
          setPlanState("none");
        }
      } catch {
        if (!cancelled) setPlanState("none");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Detección de query params del callback Strava ----
  // Sprint C Bloque 2: ya NO leemos `imported=N` del callback (la
  // importación es async). El feedback de "X actividades importadas"
  // viene del polling que StravaCard hace a /strava/import_status —
  // muestra el toast cuando el job llega a status='completed'.
  // Aquí sólo manejamos el caso de error (?strava=error&code=X).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stravaFlag = params.get("strava");
    if (!stravaFlag) return;

    if (stravaFlag === "error") {
      const code = params.get("code") ?? "";
      const message =
        code === "access_denied"
          ? t.strava.errorAccessDenied
          : t.strava.errorGeneric;
      showToast(message, "error");
    }
    // strava=connected no muestra toast aquí: el polling de StravaCard
    // se encarga del toast final cuando el job termina.

    // Limpiar query params para que el flag no quede pegado al refrescar.
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate, showToast]);

  return (
    <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone">
      <header>
        <p className="text-xs uppercase tracking-widest text-gray-mid">
          {t.app.name.toLowerCase()}
        </p>
        <h1 className="mt-6 text-2xl font-medium tracking-tight">
          {t.home.welcome}
        </h1>
        {user && (
          <p className="mt-2 text-sm text-bone-muted">{user.email}</p>
        )}
      </header>

      {/* Card "Mi plan" — visible siempre, render condicional según
          haya plan activo. Si aún no resuelto (null), placeholder
          discreto para evitar layout shift. */}
      <section
        data-testid="home-plan-card"
        aria-label={t.home.planSectionTitle}
        className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-carbon-raised p-4"
      >
        <h2 className="text-xs uppercase tracking-widest text-gray-mid">
          {t.home.planSectionTitle}
        </h2>
        {planState === null ? (
          <p className="text-sm text-bone-muted">…</p>
        ) : planState === "active" ? (
          <Link
            to="/plan"
            data-testid="home-plan-active-link"
            className="self-start rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
          >
            {t.home.planActiveButton}
          </Link>
        ) : planState === "decision" ? (
          // Sprint 13 S13.10b: recovery terminó / progresión venció → el
          // atleta debe elegir su próximo objetivo. El CTA lleva a /plan,
          // donde viven las pantallas reales de decisión (no duplicamos aquí).
          <>
            <p className="text-sm text-bone-muted">{t.home.planDecisionCta}</p>
            <Link
              to="/plan"
              data-testid="home-plan-decision-link"
              className="self-start rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
            >
              {t.home.planDecisionButton}
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm text-bone-muted">{t.home.planNoneCta}</p>
            <Link
              to="/plan/crear"
              data-testid="home-plan-create-link"
              className="self-start rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
            >
              {t.home.planNoneButton}
            </Link>
          </>
        )}
      </section>

      <StravaCard showToast={showToast} pollOnMount={stravaCallbackPending} />

      <div className="mt-auto flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => void logout()}
          className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted transition-colors duration-fast hover:border-border-strong hover:text-bone"
        >
          {t.auth.logoutCta}
        </button>
        <Link
          to="/perfil"
          data-testid="home-edit-profile-link"
          className="text-xs text-bone-muted underline-offset-4 hover:text-bone hover:underline"
        >
          {t.home.editProfileLink}
        </Link>
      </div>

      {/* Toast stack — fixed bottom-right. Aria-live para anuncio a SR. */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          className="pointer-events-none fixed bottom-toast right-4 z-50 flex max-w-xs flex-col gap-2"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              data-testid={`toast-${toast.kind}`}
              className={`pointer-events-auto rounded-md border px-3 py-2 text-sm ${
                toast.kind === "success"
                  ? "border-success/30 bg-carbon-raised text-bone"
                  : "border-danger/40 bg-carbon-raised text-bone"
              }`}
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
